import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronLeft, ChevronRight, Plus, Pencil, Trash2 } from 'lucide-react';
import { useApp } from '@/store';
import { useT } from '@/lib/i18n';
import type { I18nKey } from '@/lib/i18n';
import type { Visit } from '@/data/types';
import { VisitStatus } from '@/data/types';
import { PageHeader } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const WEEKDAYS_KEY: I18nKey[] = [
  'calendar.today', // placeholder — we use inline short names
];

const STATUS_VARIANT: Record<
  VisitStatus,
  'info' | 'warning' | 'success' | 'secondary'
> = {
  [VisitStatus.PLANNED]: 'info',
  [VisitStatus.CONFIRMED]: 'warning',
  [VisitStatus.COMPLETED]: 'success',
  [VisitStatus.CANCELLED]: 'secondary',
};

const STATUS_I18N: Record<VisitStatus, I18nKey> = {
  [VisitStatus.PLANNED]: 'calendar.status.planned',
  [VisitStatus.CONFIRMED]: 'calendar.status.confirmed',
  [VisitStatus.COMPLETED]: 'calendar.status.completed',
  [VisitStatus.CANCELLED]: 'calendar.status.cancelled',
};

const STATUS_DOT: Record<VisitStatus, string> = {
  [VisitStatus.PLANNED]: 'bg-blue-500',
  [VisitStatus.CONFIRMED]: 'bg-amber-500',
  [VisitStatus.COMPLETED]: 'bg-emerald-500',
  [VisitStatus.CANCELLED]: 'bg-muted-foreground',
};

// i18n-ignore: "Mon"
// i18n-ignore: "Tue"
// i18n-ignore: "Wed"
// i18n-ignore: "Thu"
// i18n-ignore: "Fri"
// i18n-ignore: "Sat"
// i18n-ignore: "Sun"
const WEEKDAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year: number, month: number) {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1; // Monday = 0
}

function dateKey(y: number, m: number, d: number) {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

const emptyVisit = (date: string): Visit => ({
  id: `V-${Date.now()}`,
  customerId: '',
  date,
  status: VisitStatus.PLANNED,
});

void WEEKDAYS_KEY; // suppress unused

export function CalendarPage() {
  const t = useT();
  const customers = useApp(s => s.customers);
  const visits = useApp(s => s.visits);
  const workers = useApp(s => s.workers);
  const upsertVisit = useApp(s => s.upsertVisit);
  const deleteVisit = useApp(s => s.deleteVisit);
  const toast = useApp(s => s.toast);

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [filterWorker, setFilterWorker] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editVisit, setEditVisit] = useState<Visit | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const filteredVisits = useMemo(
    () =>
      filterWorker && filterWorker !== 'all'
        ? visits.filter(v => v.worker === filterWorker)
        : visits,
    [visits, filterWorker]
  );

  const visitsByDate = useMemo(() => {
    const map: Record<string, Visit[]> = {};
    filteredVisits.forEach(v => {
      if (!map[v.date]) map[v.date] = [];
      map[v.date].push(v);
    });
    return map;
  }, [filteredVisits]);

  const prev = useCallback(() => {
    if (month === 0) {
      setMonth(11);
      setYear(y => y - 1);
    } else setMonth(m => m - 1);
  }, [month]);

  const next = useCallback(() => {
    if (month === 11) {
      setMonth(0);
      setYear(y => y + 1);
    } else setMonth(m => m + 1);
  }, [month]);

  const goToday = useCallback(() => {
    setYear(today.getFullYear());
    setMonth(today.getMonth());
  }, [today]);

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);

  // i18n-ignore: "January"
  // i18n-ignore: "February"
  // i18n-ignore: "March"
  // i18n-ignore: "April"
  // i18n-ignore: "May"
  // i18n-ignore: "June"
  // i18n-ignore: "July"
  // i18n-ignore: "August"
  // i18n-ignore: "September"
  // i18n-ignore: "October"
  // i18n-ignore: "November"
  // i18n-ignore: "December"
  const monthLabel = new Date(year, month).toLocaleString('default', {
    month: 'long',
    year: 'numeric',
  });

  const customerName = (id: string) =>
    customers.find(c => c.id === id)?.name ?? id;

  const openAdd = (date: string) => {
    setEditVisit(emptyVisit(date));
    setDialogOpen(true);
  };

  const openEdit = (v: Visit) => {
    setEditVisit({ ...v });
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (!editVisit || !editVisit.customerId) return;
    upsertVisit(editVisit);
    toast(t('calendar.visitSaved'));
    setDialogOpen(false);
    setEditVisit(null);
  };

  const handleDelete = (v: Visit) => {
    deleteVisit(v.id);
    toast(t('calendar.visitDeleted'));
  };

  const todayKey = dateKey(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  const activeWorkers = workers.filter(w => w.active);

  const dayVisits = selectedDate ? (visitsByDate[selectedDate] ?? []) : [];

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <PageHeader
        title={t('calendar.title')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Select value={filterWorker} onValueChange={setFilterWorker}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t('calendar.allWorkers')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('calendar.allWorkers')}</SelectItem>
                {activeWorkers.map(w => (
                  <SelectItem key={w.id} value={w.name}>
                    {w.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      />

      {/* Month nav */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={prev}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={next}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={goToday}>
            {t('calendar.today')}
          </Button>
        </div>
        {/* i18n-ignore: "monthLabel" */}
        <h2 className="text-lg font-semibold">{monthLabel}</h2>
      </div>

      {/* Calendar grid */}
      <Card>
        <CardContent className="p-0 sm:p-2">
          {/* Weekday headers */}
          <div className="grid grid-cols-7 sm:grid-cols-7 border-b">
            {WEEKDAY_SHORT.map(d => (
              <div
                key={d}
                className="py-2 text-center text-xs font-medium text-muted-foreground"
              >
                {d}
              </div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7 sm:grid-cols-7">
            {/* Empty cells before first day */}
            {Array.from({ length: firstDay }).map((_, i) => (
              <div
                key={`empty-${i}`}
                className="min-h-[72px] sm:min-h-[96px] border-b border-r p-1 bg-muted/20"
              />
            ))}

            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dk = dateKey(year, month, day);
              const isToday = dk === todayKey;
              const isSelected = dk === selectedDate;
              const dv = visitsByDate[dk] ?? [];

              return (
                <motion.div
                  key={dk}
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.1 }}
                  onClick={() => setSelectedDate(isSelected ? null : dk)}
                  className={`min-h-[72px] sm:min-h-[96px] border-b border-r p-1 cursor-pointer transition-colors ${
                    isSelected
                      ? 'bg-primary/10 ring-1 ring-primary'
                      : 'hover:bg-accent/40'
                  }`}
                >
                  <div className="flex items-center justify-between px-1">
                    <span
                      className={`text-xs font-medium ${
                        isToday
                          ? 'flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground'
                          : 'text-foreground'
                      }`}
                    >
                      {day}
                    </span>
                    {dv.length > 0 && (
                      <span className="text-[10px] text-muted-foreground">
                        {dv.length}
                      </span>
                    )}
                  </div>

                  {/* Visit dots */}
                  <div className="mt-1 space-y-0.5">
                    {dv.slice(0, 3).map(v => (
                      <div
                        key={v.id}
                        className="flex items-center gap-1 rounded px-1 py-0.5 text-[10px] leading-tight truncate bg-accent/50"
                      >
                        <span
                          className={`h-1.5 w-1.5 shrink-0 rounded-full ${STATUS_DOT[v.status]}`}
                        />
                        <span className="truncate hidden sm:inline">
                          {customerName(v.customerId)}
                        </span>
                      </div>
                    ))}
                    {dv.length > 3 && (
                      <span className="text-[10px] text-muted-foreground px-1">
                        +{dv.length - 3}
                      </span>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Selected day panel */}
      <AnimatePresence>
        {selectedDate && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
          >
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">{selectedDate}</h3>
                  <Button size="sm" onClick={() => openAdd(selectedDate)}>
                    <Plus className="mr-1 h-4 w-4" />
                    {t('calendar.addVisit')}
                  </Button>
                </div>

                {dayVisits.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t('calendar.noVisits')}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {dayVisits.map(v => (
                      <div
                        key={v.id}
                        className="flex flex-wrap items-center gap-2 rounded-lg border p-3"
                      >
                        <Badge variant={STATUS_VARIANT[v.status]}>
                          {t(STATUS_I18N[v.status])}
                        </Badge>
                        <Link
                          to={`/customer/${v.customerId}`}
                          className="font-medium text-sm text-primary hover:underline"
                        >
                          {customerName(v.customerId)}
                        </Link>
                        {v.worker && (
                          <span className="text-sm text-muted-foreground">
                            — {v.worker}
                          </span>
                        )}
                        {v.notes && (
                          <span className="text-sm text-muted-foreground italic basis-full">
                            {v.notes}
                          </span>
                        )}
                        <div className="ml-auto flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => openEdit(v)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive"
                            onClick={() => handleDelete(v)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editVisit && visits.some(v => v.id === editVisit.id)
                ? t('calendar.editVisit')
                : t('calendar.addVisit')}
            </DialogTitle>
          </DialogHeader>

          {editVisit && (
            <div className="grid grid-cols-1 gap-4 py-2">
              <div className="space-y-1.5">
                <Label>{t('calendar.customer')}</Label>
                <Select
                  value={editVisit.customerId}
                  onValueChange={v =>
                    setEditVisit({ ...editVisit, customerId: v })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('calendar.selectCustomer')} />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>{t('calendar.date')}</Label>
                <Input
                  type="date"
                  value={editVisit.date}
                  onChange={e =>
                    setEditVisit({ ...editVisit, date: e.target.value })
                  }
                />
              </div>

              <div className="space-y-1.5">
                <Label>{t('calendar.worker')}</Label>
                <Select
                  value={editVisit.worker ?? ''}
                  onValueChange={v => setEditVisit({ ...editVisit, worker: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('calendar.worker')} />
                  </SelectTrigger>
                  <SelectContent>
                    {activeWorkers.map(w => (
                      <SelectItem key={w.id} value={w.name}>
                        {w.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>{t('calendar.status')}</Label>
                <Select
                  value={editVisit.status}
                  onValueChange={v =>
                    setEditVisit({
                      ...editVisit,
                      status: v as VisitStatus,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(VisitStatus).map(s => (
                      <SelectItem key={s} value={s}>
                        {t(STATUS_I18N[s])}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>{t('calendar.notes')}</Label>
                <Textarea
                  value={editVisit.notes ?? ''}
                  onChange={e =>
                    setEditVisit({ ...editVisit, notes: e.target.value })
                  }
                  rows={2}
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  {t('calendar.cancel')}
                </Button>
                <Button onClick={handleSave} disabled={!editVisit.customerId}>
                  {t('calendar.save')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
